// ===========================================
// MAKER / CPG DASHBOARD — forecast-first
// ===========================================
// A wholesale juice maker's #1 need is understanding the forecast: are we on
// pace, what's left to sell, what to make. Everything else is secondary. This
// leads with forecast-vs-actual (per SKU), then a small visibility strip, then
// where every number came from.

import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { FileSpreadsheet, ArrowRight, Download, UploadCloud, Loader2, Camera, Wallet, ClipboardList, Factory, TrendingUp } from 'lucide-react';
import { wholesaleApi, productionApi, restaurantApi, menuApi } from '../services/api';
import ActionList from './ActionList';
import ForecastChart, { type OverviewSeries } from './ForecastChart';
import MakerAnalytics from './MakerAnalytics';
import YearlyBudget from './YearlyBudget';
import CategorySplit from './CategorySplit';
import { channelMoney } from '../utils/channelMoney';

const money = (cents: number) => `$${Math.round(cents / 100).toLocaleString('en-US')}`;
const MONTH_ELAPSED = 8; // Jan–Aug actuals; keep in sync with stored forecast
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Connected-channel sales (Square, Uber Eats, DoorDash) — the summaries the
// syncs store. Renders nothing until a channel has numbers.
function ChannelSalesCard() {
  const { data } = useQuery({
    queryKey: ['channel-sales-summary'],
    queryFn: async () => (await wholesaleApi.getSalesSummary()).data?.data || { sources: [] },
    staleTime: 60_000,
  });
  const sources = (data?.sources || []).flatMap((s0: any) => s0.platform === 'shopify'
    ? (s0.currencyBasis === 'customer' ? (s0.currencies || []).map((group: any) => ({ ...s0, ...group, label: `Shopify · ${group.currency}` })) : [])
    : [s0]).filter((s0: any) => (s0.days || []).length > 0);
  if (!sources.length) return null;
  return (
    <div className="card p-5">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-primary-600" />
        <h2 className="font-semibold text-slate-900">Sales — last 7 days</h2>
      </div>
      <div className="space-y-3">
        {sources.map((s0: any) => {
          const money = (c: number) => channelMoney(c / 100, s0.currency || (s0.platform === 'shopify' ? null : 'USD'));
          const total = (s0.days || []).reduce((a: number, d: any) => a + (d.grossCents || 0), 0);
          const count = (s0.days || []).reduce((a: number, d: any) => a + (d.payments || d.orders || 0), 0);
          const best = Math.max(1, ...(s0.days || []).map((d: any) => d.grossCents || 0));
          return (
            <div key={`${s0.platform}-${s0.currency || 'default'}`}>
              <div className="flex items-baseline justify-between">
                <p className="text-sm font-medium text-slate-900">{s0.label}
                  {(s0.locationNames || s0.storeNames || []).length > 0 && (
                    <span className="text-xs text-slate-400 font-normal ml-2">{(s0.locationNames || s0.storeNames).join(', ')}</span>
                  )}
                </p>
                <p className="text-sm font-semibold text-slate-900">{money(total)} <span className="text-xs text-slate-400 font-normal">· {count} sales</span></p>
              </div>
              <div className="flex items-end gap-1 mt-2 h-8">
                {[...(s0.days || [])].sort((a: any, b: any) => a.date.localeCompare(b.date)).map((d: any) => (
                  <div key={d.date} className="flex-1 bg-primary-100 rounded-sm" title={`${d.date}: ${money(d.grossCents)}`}
                    style={{ height: `${Math.max(12, Math.round(((d.grossCents || 0) / best) * 100))}%` }} />
                ))}
              </div>
              {s0.platform === 'shopify' && <div className="flex flex-wrap gap-4 mt-3 text-sm font-medium text-primary-600">
                <Link to="/production?tab=orders">View open orders →</Link>
                <Link to="/products">View Shopify products →</Link>
              </div>}
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-400 mt-3">Straight from your connected channels — refreshes on every sync.</p>
    </div>
  );
}

// The next fulfilment day at a glance — for bakeries whose orders flow in
// from a store (Braided's Friday bake). Nothing to make → renders nothing.
function NextBakeCard() {
  const { data: plan } = useQuery({
    queryKey: ['production-plan-nextbake'],
    queryFn: async () => (await productionApi.getPlan()).data?.data || null,
    staleTime: 60_000,
  });
  const dates: Array<{ value: string; orders: number }> = plan?.options?.dates || [];
  const today = new Date().toISOString().slice(0, 10);
  const next = dates.filter((d) => d.value >= today).sort((a, b) => a.value.localeCompare(b.value))[0];
  const { data: dayPlan } = useQuery({
    queryKey: ['production-plan-nextbake-day', next?.value],
    queryFn: async () => (await productionApi.getPlan({ date: next!.value })).data?.data || null,
    enabled: !!next,
    staleTime: 60_000,
  });
  if (!next || !dayPlan || !dayPlan.totalUnits) return null;
  const label = new Date(next.value + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  return (
    <Link to="/production?tab=plan" className="card p-5 block hover:bg-slate-50 active:scale-[0.99] transition-transform">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Factory className="w-4 h-4 text-primary-600" />
          <h2 className="font-semibold text-slate-900">Next bake — {label}</h2>
        </div>
        <span className="text-sm font-semibold text-slate-900">{dayPlan.totalUnits} units <span className="text-xs text-slate-400 font-normal">· {dayPlan.orderCount} orders</span></span>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {(dayPlan.byClass || []).map((c: any) => (
          <span key={c.name} className="text-xs bg-slate-100 text-slate-700 rounded-full px-2.5 py-1">{c.quantity} {c.name}</span>
        ))}
        {(dayPlan.exceptions || []).length > 0 && (
          <span className="text-xs bg-amber-50 text-amber-700 rounded-full px-2.5 py-1">{dayPlan.exceptions.length} unmapped</span>
        )}
        {dayPlan.expectedUnits > 0 && (
          <span className="text-xs bg-slate-50 text-slate-500 rounded-full px-2.5 py-1">{dayPlan.expectedUnits} expected from subscriptions</span>
        )}
      </div>
      <p className="text-[11px] text-slate-400 mt-2.5">Tap for the full breakdown, labels and the email-to-the-team button.</p>
    </Link>
  );
}

// The first two minutes: drop the menu you already have, watch Mise build
// itself. The aha moment, on the welcome screen itself — no navigation.
function MenuDropzone() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const imp = useMutation({
    mutationFn: (f: File) => menuApi.importMenu(f),
    onSuccess: (res) => {
      const d = res.data?.data || {};
      qc.invalidateQueries({ queryKey: ['menu-items-flat'] });
      qc.invalidateQueries({ queryKey: ['menu-categories'] });
      setResult(`Read it — ${d.items || 0} items in ${d.categories || 0} categories. Your menu is live; give the prices a once-over.`);
    },
    onError: (e: any) => setResult(e?.response?.data?.error?.message || "Couldn't read that one — a clearer photo or the original PDF usually works."),
  });
  const take = (f?: File | null) => { if (f) { setResult(null); imp.mutate(f); } };
  return (
    <div>
      <input ref={fileRef} type="file" accept=".pdf,.csv,image/*" className="hidden"
        onChange={(e) => { take(e.target.files?.[0]); e.target.value = ''; }} />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); take(e.dataTransfer.files?.[0]); }}
        className={`w-full rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${drag ? 'border-primary-500 bg-primary-50' : 'border-primary-200 bg-primary-50/50 hover:border-primary-400'}`}
      >
        {imp.isPending ? (
          <span className="flex items-center justify-center gap-2 text-primary-700 font-medium"><Loader2 className="w-5 h-5 animate-spin" /> Reading your menu…</span>
        ) : (
          <>
            <UploadCloud className="w-8 h-8 mx-auto text-primary-500 mb-2" />
            <p className="font-semibold text-slate-900">Drop your menu here</p>
            <p className="text-sm text-slate-500 mt-1">A PDF, a photo of the printed menu, or a spreadsheet — Mise reads it and builds your menu, prices and all.</p>
          </>
        )}
      </button>
      {result && (
        <div className="mt-2 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm px-4 py-3 flex items-center justify-between gap-2">
          <span>{result}</span>
          <Link to="/menu" className="font-medium shrink-0">See it →</Link>
        </div>
      )}
    </div>
  );
}

// Quiet progress toward actually running: three real checks, gone forever
// once all three are true. Derived from live data — never a fake checklist.
function SetupProgress({ menuCount }: { menuCount: number }) {
  const { data: sales } = useQuery({
    queryKey: ['channel-sales-summary'],
    queryFn: async () => (await wholesaleApi.getSalesSummary()).data?.data || { sources: [] },
    staleTime: 60_000,
  });
  const { data: shopify } = useQuery({
    queryKey: ['production-shopify'],
    queryFn: async () => (await productionApi.getShopifyStatus()).data?.data || { connected: false },
    staleTime: 60_000,
  });
  const { data: square } = useQuery({
    queryKey: ['square-status'],
    queryFn: async () => (await wholesaleApi.getSquareStatus()).data?.data || { connected: false },
    staleTime: 60_000,
  });
  const { data: recipes } = useQuery({
    queryKey: ['recipes-count-setup'],
    queryFn: async () => { const r = await (await import('../services/api')).recipesApi.getAll(); const d = r.data?.data || r.data; return (Array.isArray(d) ? d : d?.recipes || []).length; },
    staleTime: 60_000,
  });
  const steps = [
    { done: menuCount > 0, label: 'Your menu is in', to: '/menu', hint: 'drop a PDF or import from your store' },
    { done: !!(shopify?.connected || square?.connected || (sales?.sources || []).length), label: 'Sales flow in on their own', to: '/settings?tab=integrations', hint: 'connect Square or Shopify' },
    { done: (recipes || 0) > 0, label: 'You know what one batch costs', to: '/recipes', hint: 'cost your first recipe' },
  ];
  if (steps.every((x) => x.done)) return null;
  return (
    <div className="card p-4">
      <p className="text-sm font-semibold text-slate-900 mb-2">Getting set up · {steps.filter((x) => x.done).length} of {steps.length}</p>
      <div className="space-y-1.5">
        {steps.map((st) => (
          <Link key={st.label} to={st.to} className="flex items-center gap-2.5 text-sm group">
            <span className={`w-4.5 h-4.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${st.done ? 'bg-emerald-500 text-white' : 'border border-slate-300'}`}>
              {st.done && <span className="text-[10px]">✓</span>}
            </span>
            <span className={st.done ? 'text-slate-400 line-through' : 'text-slate-700 group-hover:text-primary-700'}>{st.label}</span>
            {!st.done && <span className="text-xs text-slate-400">— {st.hint}</span>}
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function MakerDashboard({ restaurantName }: { restaurantName?: string }) {
  const qc = useQueryClient();
  const { data: restaurant } = useQuery({ queryKey: ['restaurant-forecast'], queryFn: async () => (await restaurantApi.getCurrent()).data?.data || null });
  // Source files — the actual documents this account was set up from
  const { data: files = [] } = useQuery({ queryKey: ['source-files'], queryFn: async () => (await restaurantApi.getFiles()).data?.data || [] });
  const fileRef = useRef<HTMLInputElement>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: (f: File) => restaurantApi.uploadFile(f),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['source-files'] }),
  });
  const download = async (f: { id: string; name: string }) => {
    setDownloading(f.id);
    try {
      const res = await restaurantApi.downloadFile(f.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = f.name; a.click();
      URL.revokeObjectURL(url);
    } finally { setDownloading(null); }
  };
  const { data: orders = [], isPending: ordersPending } = useQuery({ queryKey: ['wholesale-orders-dash'], queryFn: async () => (await wholesaleApi.getOrders()).data?.data || [] });
  const { data: invoices = [], isPending: invoicesPending } = useQuery({ queryKey: ['wholesale-invoices-dash'], queryFn: async () => (await wholesaleApi.getInvoices()).data?.data || [] });
  const { data: plan } = useQuery({ queryKey: ['production-plan-dash'], queryFn: async () => (await productionApi.getPlan()).data?.data || null });

  const orderList: any[] = Array.isArray(orders) ? orders : (orders as any).orders || [];
  const invList: any[] = Array.isArray(invoices) ? invoices : (invoices as any).invoices || [];
  const forecast = restaurant?.settings?.forecast as { skus?: Record<string, { name: string; fy: number; ytd: number; remaining: number; monthly?: number[] }> } | undefined;
  const skus = forecast?.skus ? Object.entries(forecast.skus).map(([code, k]) => ({ code, ...k })).sort((a, b) => b.fy - a.fy) : [];

  // SKU × month: actual cases delivered per month (from orders), vs the monthly forecast.
  const { data: menuData } = useQuery({ queryKey: ['menu-items-flat'], queryFn: async () => { const res = await menuApi.getItems(); return res.data?.data || res.data || []; }, staleTime: 60_000 });
  const idToCode: Record<string, string> = {};
  const priceByCode: Record<string, number> = {};
  for (const m of (Array.isArray(menuData) ? menuData : (menuData as any)?.items || [])) {
    const mm = /SKU (\d+)/.exec(m?.description || '');
    if (mm && m?.id) { idToCode[m.id] = mm[1]; priceByCode[mm[1]] = m?.priceCents || 0; }
  }
  const actualMonthly: Record<string, number[]> = {};
  for (const o of orderList) {
    if (o.status === 'cancelled' || o.onConsignment || !o.fulfillmentDate) continue;
    if (/case count n\/a/i.test(o.notes || '')) continue; // depletion billing: dollars, not cases
    const d = new Date(o.fulfillmentDate);
    if (d.getFullYear() !== 2026) continue;
    for (const it of o.items || []) {
      const code = idToCode[it.menuItemId];
      if (!code) continue;
      (actualMonthly[code] ||= Array(12).fill(0))[d.getMonth()] += parseFloat(String(it.quantity)) || 0;
    }
  }
  const [openSku, setOpenSku] = useState<string | null>(null);

  const fyTotal = skus.reduce((s, k) => s + k.fy, 0);
  const soldTotal = skus.reduce((s, k) => s + k.ytd, 0);
  const remainingTotal = skus.reduce((s, k) => s + k.remaining, 0);
  const pct = fyTotal ? Math.round((soldTotal / fyTotal) * 100) : 0;

  // Pace against the PLAN's own shape when we have it, not a straight line.
  // A back-loaded forecast (big Q4) makes straight-line pace read as "behind"
  // in August even when the business is dead on plan.
  const overview = restaurant?.settings?.overviewSeries as OverviewSeries | undefined;
  const planToDate = overview ? overview.total.plan.slice(0, overview.actualThrough).reduce((s, v) => s + (v || 0), 0) : 0;
  const actualToDate = overview ? overview.total.actual.reduce((s: number, v) => s + (v || 0), 0) : 0;
  const paceOfPlan = overview && planToDate ? Math.round((actualToDate / planToDate) * 100) : null;
  const expectedPct = overview && fyTotal ? Math.round((planToDate / fyTotal) * 100) : Math.round((MONTH_ELAPSED / 12) * 100);
  const onPace = paceOfPlan != null ? paceOfPlan >= 95 : pct >= expectedPct - 3;
  const paceLabel = paceOfPlan != null
    ? `${paceOfPlan}% of plan through ${MONTHS[Math.min(11, (overview!.actualThrough || 1) - 1)]}`
    : (onPace ? 'On pace' : `Behind pace (${expectedPct}% expected by now)`);

  // Active orders only — the same basis as Wholesale/Channels, so every screen agrees.
  // Consignment shipments are parked stock, not sales: excluded from revenue.
  // Revenue is recognized on DELIVERY (Tassilo's rule): an open or in-transit
  // PO isn't a sale yet — it counts once delivered (invoiced/paid rows predate
  // the delivered flag and count too).
  const activeOrders = orderList.filter((o) => o.status !== 'cancelled');
  const salesOrders = activeOrders.filter((o) => !o.onConsignment);
  const recognized = (o: any) => o.freightDelivered || o.invoiceSent || o.paymentReceived;
  const revenue = salesOrders.filter(recognized).reduce((s, o) => s + (o.totalCents || 0), 0);
  const arOutstanding = invList.filter((i) => i.status !== 'paid' && i.status !== 'void').reduce((s, i) => s + (i.totalCents || 0) - (i.amountPaidCents || 0), 0);
  // Oldest unpaid invoice age — the number an operator actually chases.
  const oldestArDays = invList
    .filter((i) => i.status !== 'paid' && i.status !== 'void')
    .reduce((mx, i) => { const t = new Date(i.sentAt || i.issuedAt || i.createdAt).getTime(); return Number.isFinite(t) ? Math.max(mx, Math.floor((Date.now() - t) / 86400000)) : mx; }, 0);
  const distOrders = activeOrders.filter((o) => o.poNumber || /PO /.test(o.notes || '')).length;
  const directOrders = activeOrders.filter((o) => /Direct delivery/.test(o.notes || '')).length;

  // The day's verbs, as big touchable cards. Data-driven: a PO business gets the
  // PO workflow; a maker without POs gets its production actions. Calm, no clutter.
  const quickActions = distOrders > 0
    ? [
        { icon: Camera, label: 'Scan a PO', sub: 'photo becomes an order', to: '/wholesale?tab=orders&scan=1' },
        { icon: Wallet, label: 'Money owed', sub: arOutstanding > 0 ? `${money(arOutstanding)} · oldest ${oldestArDays}d` : money(0), to: '/wholesale?tab=invoices' },
        { icon: ClipboardList, label: 'My POs', sub: 'where each one is', to: '/wholesale' },
        { icon: Factory, label: 'What to make', sub: 'press & pallets', to: '/production?tab=supply' },
      ]
    : [
        { icon: Factory, label: 'What to make', sub: "today's plan", to: '/production' },
        { icon: ClipboardList, label: 'Orders', sub: 'add or review', to: '/production?tab=orders' },
      ];

  // FRESH CPG ACCOUNT — nothing imported yet. The forecast-first dashboard is
  // all zeros here, so instead: a calm welcome that points at the three ways to
  // bring orders in. Disappears forever after the first order/invoice/forecast.
  const producing = (plan?.orderCount || 0) > 0;
  const fresh = !ordersPending && !invoicesPending && orderList.length === 0 && invList.length === 0 && skus.length === 0 && !producing;
  const retail = restaurant?.settings?.modules?.wholesale === false;
  if (fresh && retail) return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-5 pb-safe">
      <ActionList />
      <ChannelSalesCard />
      <div className="pt-2">
        <h1 className="text-xl md:text-2xl font-bold text-slate-900">Welcome{restaurantName ? ` — ${restaurantName}` : ''}</h1>
        <p className="text-sm text-slate-500 mt-1">Your workspace is ready. Connect the till and set the kitchen up — everything builds from there.</p>
      </div>
      <MenuDropzone />
      <div className="grid sm:grid-cols-3 gap-3">
        <Link to="/settings?tab=integrations" className="card p-5 flex flex-col gap-2 active:scale-[0.98] transition-transform hover:bg-slate-50">
          <Wallet className="w-6 h-6 text-primary-600" />
          <p className="font-semibold text-slate-900 text-sm">Connect your POS</p>
          <p className="text-xs text-slate-500">Square or Shopify — your sales show up right here, updated through the day.</p>
        </Link>
        <Link to="/recipes" className="card p-5 flex flex-col gap-2 active:scale-[0.98] transition-transform hover:bg-slate-50">
          <ClipboardList className="w-6 h-6 text-primary-600" />
          <p className="font-semibold text-slate-900 text-sm">Cost a recipe</p>
          <p className="text-xs text-slate-500">Put in what goes into a batch — see what it truly costs against what it sells for.</p>
        </Link>
        <Link to="/ordering" className="card p-5 flex flex-col gap-2 active:scale-[0.98] transition-transform hover:bg-slate-50">
          <Factory className="w-6 h-6 text-primary-600" />
          <p className="font-semibold text-slate-900 text-sm">Set a standing order</p>
          <p className="text-xs text-slate-500">Your weekly supplier order, drafted automatically — you review and send.</p>
        </Link>
      </div>
      <p className="text-[11px] text-slate-400">Nothing here is sample data — this page fills in as your real sales and orders do. Want a different mix of features? <Link to="/settings?tab=modules" className="text-primary-600">Turn any part of Mise on or off</Link>.</p>
    </div>
  );
  if (fresh) return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-5 pb-safe">
      {/* Quote requests & inbox to-dos outrank the welcome — a fresh account
          with a live catalog gets requests before it has orders. */}
      <ActionList />
      <ChannelSalesCard />
      <NextBakeCard />
      <div className="pt-2">
        <h1 className="text-xl md:text-2xl font-bold text-slate-900">Welcome{restaurantName ? ` — ${restaurantName}` : ''}</h1>
        <p className="text-sm text-slate-500 mt-1">Your workspace is ready. Bring in your first orders — everything else builds from them.</p>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <Link to="/wholesale?tab=orders&scan=1" className="card p-5 flex flex-col gap-2 active:scale-[0.98] transition-transform hover:bg-slate-50">
          <Camera className="w-6 h-6 text-primary-600" />
          <p className="font-semibold text-slate-900 text-sm">Scan a purchase order</p>
          <p className="text-xs text-slate-500">Photograph or upload a PO — the products, quantities and pricing become an order automatically.</p>
        </Link>
        <Link to="/wholesale?tab=orders&import=1" className="card p-5 hidden sm:flex flex-col gap-2 active:scale-[0.98] transition-transform hover:bg-slate-50">
          <FileSpreadsheet className="w-6 h-6 text-primary-600" />
          <p className="font-semibold text-slate-900 text-sm">Import your spreadsheet</p>
          <p className="text-xs text-slate-500">Bring your order tracker over as-is — customers and orders come with it.</p>
        </Link>
        <Link to="/wholesale?tab=orders" className="card p-5 flex flex-col gap-2 active:scale-[0.98] transition-transform hover:bg-slate-50">
          <ClipboardList className="w-6 h-6 text-primary-600" />
          <p className="font-semibold text-slate-900 text-sm">Add one by hand</p>
          <p className="text-xs text-slate-500">Type in an order or two and watch it move through the pipeline.</p>
        </Link>
      </div>
      <div className="card p-5">
        <h2 className="font-semibold text-slate-900 mb-2">What happens from there</h2>
        <ol className="space-y-1.5 text-sm text-slate-600 list-none">
          <li><span className="font-medium text-slate-900">1 · Orders</span> — every PO tracked from open → shipped → delivered → invoiced → paid.</li>
          <li><span className="font-medium text-slate-900">2 · Invoices</span> — one tap per delivered order; short-pays close as deductions, nothing slips.</li>
          <li><span className="font-medium text-slate-900">3 · Production</span> — <Link to="/production" className="text-primary-600 font-medium">What to make</Link> works backwards from the orders you owe.</li>
        </ol>
      </div>
      <p className="text-sm text-slate-500">
        Already selling on Shopify, Faire or Square? <Link to="/settings?tab=integrations" className="text-primary-600 font-medium">Connect it</Link> — orders flow in on their own. QuickBooks too, for the books.
      </p>
      <p className="text-[11px] text-slate-400">Nothing here is sample data — this page fills in as your real orders do. Want a different mix of features? <Link to="/settings?tab=modules" className="text-primary-600">Turn any part of Mise on or off</Link>.</p>
    </div>
  );

  return (
    <div className="p-4 md:p-6 space-y-5 pb-safe">
      <ChannelSalesCard />
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-slate-900">{restaurantName || 'Dashboard'}</h1>
        <p className="text-sm text-slate-500">{skus.length > 0 ? 'How the year is tracking against your forecast' : producing ? 'This week\'s sales and open orders, at a glance' : 'Today, at a glance'}</p>
      </div>

      {/* The day's actions — one tap each */}
      <div className={`grid gap-3 ${quickActions.length > 2 ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-2'}`}>
        {quickActions.map((a) => (
          <Link key={a.label} to={a.to}
            className="card p-4 flex flex-col gap-2 active:scale-[0.98] transition-transform hover:bg-slate-50">
            <a.icon className="w-5 h-5 text-primary-600" />
            <div>
              <p className="font-semibold text-slate-900 text-sm">{a.label}</p>
              <p className="text-xs text-slate-500">{a.sub}</p>
            </div>
          </Link>
        ))}
      </div>

      {/* Inbox-fed to-dos — only renders when something needs doing */}
      <ActionList />

      {/* The next fulfilment day, when a store feeds production */}
      <NextBakeCard />

      {/* Until running: menu in, sales connected, first cost known */}
      {retail && <SetupProgress menuCount={(Array.isArray(menuData) ? menuData : (menuData as any)?.items || []).length} />}

      {/* FORECAST — the hero */}
      {skus.length > 0 ? (
        <div className="card p-5">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div>
              <p className="text-xs uppercase text-slate-400 mb-1">Sold vs. full-year forecast</p>
              <p className="text-3xl font-bold text-slate-900">{pct}%<span className="text-base font-medium text-slate-400"> of forecast</span></p>
              <p className="text-sm text-slate-500">{soldTotal.toLocaleString('en-US')} of {fyTotal.toLocaleString('en-US')} cases · {remainingTotal.toLocaleString('en-US')} to go</p>
            </div>
            <span className={`text-sm font-medium px-3 py-1.5 rounded-full ${onPace ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
              {paceLabel}
            </span>
          </div>
          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden mb-5">
            <div className={`h-full rounded-full ${onPace ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
          </div>
          <div className="space-y-3">
            {skus.map((k) => {
              const p = k.fy ? Math.round((k.ytd / k.fy) * 100) : 0;
              const behind = p < expectedPct - 5;
              const isOpen = openSku === k.code;
              const actual = actualMonthly[k.code];
              return (
                <div key={k.name}>
                  <button onClick={() => setOpenSku(isOpen ? null : k.code)} className="w-full text-left" title="Tap for month-by-month">
                    <div className="flex flex-wrap items-center justify-between gap-x-3 text-sm mb-1">
                      <span className="text-slate-700">{k.name}</span>
                      <span className="text-slate-500"><span className="font-semibold text-slate-900">{k.ytd.toLocaleString('en-US')}</span> / {k.fy.toLocaleString('en-US')} · {p}% · <span className={behind ? 'text-amber-600' : 'text-slate-500'}>{k.remaining.toLocaleString('en-US')} left</span></span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full rounded-full ${behind ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(100, p)}%` }} />
                    </div>
                  </button>
                  {isOpen && (
                    <div className="mt-2 mb-1">
                      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-1.5">
                        {MONTHS.map((m, i) => {
                          const act = actual ? Math.round(actual[i]) : 0;
                          const plan = k.monthly?.[i] ?? null;
                          return (
                            <div key={m} className="rounded-lg bg-slate-50 border border-slate-100 px-1.5 py-1 text-center">
                              <p className="text-[10px] text-slate-400">{m}</p>
                              <p className="text-xs font-semibold text-slate-900">{act ? act.toLocaleString('en-US') : '—'}</p>
                              {plan != null && <p className="text-[10px] text-slate-400">of {plan.toLocaleString('en-US')}</p>}
                            </div>
                          );
                        })}
                      </div>
                      {(() => {
                        // Next year's monthly plan for this SKU, when loaded
                        const m27 = (restaurant?.settings?.forecast2027 as any)?.skus?.[k.code]?.monthly as number[] | undefined;
                        if (!m27?.some((v) => v)) return null;
                        return (
                          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-1.5 mt-1.5">
                            {MONTHS.map((m, i) => (
                              <div key={m} className="rounded-lg bg-blue-50/60 border border-blue-100 px-1.5 py-1 text-center">
                                <p className="text-[10px] text-blue-400">{m} ’27</p>
                                <p className="text-xs font-semibold text-blue-900">{m27[i] ? Math.round(m27[i]).toLocaleString('en-US') : '—'}</p>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                      <p className="text-[11px] text-slate-400 mt-1.5">Cases on recorded orders each month, vs your forecast{(restaurant?.settings?.forecast2027 as any)?.skus?.[k.code] ? '; blue row = next year\'s plan' : ''}. The yearly figure above is from your forecast sheet, which also counts sales without an order on file.</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : producing ? null : (
        <div className="card p-6 text-center text-slate-400">Forecast not loaded yet.</div>
      )}

      {/* THE CHARTS FROM THEIR OWN SPREADSHEET — plan vs actual vs last year,
          monthly + cumulative, whole business or one channel. Data-driven:
          hidden entirely unless the account has imported chart series. */}
      {overview && <ForecastChart series={overview} />}

      {/* VISIBILITY strip — wholesale money at a glance. A production-led shop
          with no wholesale activity would just see a row of zeros — skip it. */}
      {(revenue > 0 || arOutstanding > 0 || !producing) && (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="card p-4"><p className="text-xs uppercase text-slate-400 mb-1">Revenue (YTD)</p><p className="text-2xl font-bold text-slate-900">{money(revenue)}</p></div>
        <div className="card p-4"><p className="text-xs uppercase text-slate-400 mb-1">Outstanding AR</p><p className="text-2xl font-bold text-slate-900">{money(arOutstanding)}</p>{arOutstanding === 0 && <p className="text-xs text-emerald-600">All settled</p>}</div>
        <div className="card p-4"><p className="text-xs uppercase text-slate-400 mb-1">Orders</p><p className="text-2xl font-bold text-slate-900">{activeOrders.length}</p></div>
        <div className="card p-4"><p className="text-xs uppercase text-slate-400 mb-1">To make (rest of year)</p><p className="text-2xl font-bold text-slate-900">{(plan?.totalUnits || remainingTotal).toLocaleString('en-US')}</p><p className="text-xs text-slate-400">cases</p></div>
      </div>
      )}

      {/* Revenue split by sales category — self-hiding until customers carry
          a category; the full breakdown lives on Wholesale → Channels. */}
      <CategorySplit compact />

      {/* Yearly budget — annual revenue plan vs actual by SKU (forecast × price).
          Hides itself when the account has no forecast. */}
      <YearlyBudget skus={skus} priceByCode={priceByCode} year={2026} />

      {/* Analytics — what sells, who buys, what's owed, where orders sit.
          All from the orders + invoices already loaded; hides itself when empty. */}
      <MakerAnalytics orders={orderList} invoices={invList} />

      {/* Where this comes from — provenance / trust. Only for accounts that
          actually imported spreadsheets; a POS-connected venue's provenance is
          the live connection itself. */}
      {(distOrders + directOrders + invList.length > 0 || files.length > 0) && (
      <div className="card p-5">
        <div className="flex items-center gap-2 mb-1">
          <FileSpreadsheet className="w-4 h-4 text-slate-500" />
          <h2 className="font-semibold text-slate-900">Where this comes from</h2>
        </div>
        <p className="text-xs text-slate-500 mb-3">Every number traces to its source — tap a count to open the underlying rows.</p>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div className="flex items-center justify-between"><span className="text-slate-600">Production → what to make</span><Link to="/production" className="font-semibold text-primary-600">plan</Link></div>
          <div className="flex items-center justify-between"><span className="text-slate-600">Wholesale → orders</span><Link to="/wholesale" className="font-semibold text-primary-600">{distOrders}</Link></div>
          <div className="flex items-center justify-between"><span className="text-slate-600">Direct deliveries</span><Link to="/wholesale" className="font-semibold text-primary-600">{directOrders}</Link></div>
          <div className="flex items-center justify-between"><span className="text-slate-600">Invoices &amp; receivables</span><Link to="/wholesale" className="font-semibold text-primary-600">{invList.length}</Link></div>
        </div>
        {files.length > 0 && (<p className="text-[11px] text-slate-400 mt-3">Forecast &amp; actuals come from your imported sheets — the originals are kept below so any number can be traced.</p>)}

        {/* The original documents, kept in-app so any number can be traced to its source.
            Hidden entirely until files exist, so accounts without them see no clutter. */}
        {files.length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-slate-700">Your source files</p>
            <button onClick={() => fileRef.current?.click()} disabled={upload.isPending} className="text-xs text-primary-600 font-medium flex items-center gap-1">
              {upload.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />} Add a file
            </button>
            <input ref={fileRef} type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ''; }} />
          </div>
          <div className="grid sm:grid-cols-2 gap-1.5">
            {files.map((f: any) => (
              <button key={f.id} onClick={() => download(f)} className="flex items-center gap-2 text-left text-xs rounded-lg border border-slate-100 px-2.5 py-2 hover:bg-slate-50">
                {downloading === f.id ? <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400 shrink-0" /> : <Download className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                <span className="truncate text-slate-700">{f.name}</span>
                <span className="ml-auto text-slate-400 shrink-0">{f.size > 1048576 ? `${(f.size / 1048576).toFixed(1)} MB` : `${Math.round(f.size / 1024)} KB`}</span>
              </button>
            ))}
          </div>
        </div>
        )}
      </div>
      )}
    </div>
  );
}
